import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Redirect } from "react-router-dom";
import useSWR from "swr";

import "./style.scss";
import LoanRequestFilter from "./components/LoanRequestFilter";
import GeographyFilters from "./components/GeographyFilters";
import LoanRequestsTable from "./components/LoanRequestTable";
import LoanApprovalModal from "./components/LoanApprovalModal";
import ActionReasonsModal from "./components/ActionReasonsModal";
import { fetcher } from "../../utilities/_fetcher";
import { buildMerchantLoanListUrl } from "./service/loanService";

function MerchantLoanRequests() {
  const [focusedLoan, setFocusedLoan] = useState(null);
  const [focusedLoanIndex, setFocusedLoanIndex] = useState(-1);
  const [activeTab, setActiveTab] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isActionReasonModalOpen, setIsActionReasonModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [counts, setCounts] = useState({
    total: 0,
    mdo_pending: 0,
    om_pending: 0,
  });

  const [geographyFilters, setGeographyFilters] = useState({
    clusters: [],
    agency: [],
    region: [],
    area: [],
    territory: [],
  });

  const prevFiltersRef = useRef({ activeTab, searchTerm, geographyFilters });
  const filtersChangedRef = useRef(false);

  const apiUrl = useMemo(() => {
    const prevFilters = prevFiltersRef.current;
    const currentFilters = { activeTab, searchTerm, geographyFilters };

    const hasFiltersChanged =
      prevFilters.activeTab !== activeTab ||
      prevFilters.searchTerm !== searchTerm ||
      JSON.stringify(prevFilters.geographyFilters) !==
        JSON.stringify(geographyFilters);

    filtersChangedRef.current = hasFiltersChanged;

    prevFiltersRef.current = currentFilters;

    const limit = 10;

    const pageToUse = hasFiltersChanged ? 1 : currentPage;

    return buildMerchantLoanListUrl({
      pageNo: pageToUse,
      pageSize: limit,
      approvedType: activeTab,
      search: searchTerm,
      geographyFilters,
    });
  }, [activeTab, geographyFilters, searchTerm, currentPage]);

  const {
    data: response,
    error,
    mutate: mutateLoanList,
  } = useSWR(apiUrl, fetcher, {
    fallbackData: {},
    onSuccess: (data) => {
      setData(data.data || []);
      setPageCount(data.num_pages || 1);
      setCounts(data.count || { total: 0, mdo_pending: 0, om_pending: 0 });
    },
    onError: (err) => {
      setData([]);
      setPageCount(1);
      setCounts({ total: 0, mdo_pending: 0, om_pending: 0 });
    },
    revalidateOnFocus: false,
    refreshInterval: 5 * 60 * 1000,
  });

  const loading = !response && !error;

  useEffect(() => {
    if (filtersChangedRef.current && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [response, currentPage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (data.length === 0) return;

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedLoanIndex((prev) => {
          const newIndex = prev <= 0 ? 0 : prev - 1;
          setFocusedLoan(data[newIndex]);
          return newIndex;
        });
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedLoanIndex((prev) => {
          const newIndex = prev >= data.length - 1 ? data.length - 1 : prev + 1;
          setFocusedLoan(data[newIndex]);
          return newIndex;
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [data, focusedLoanIndex]);

  useEffect(() => {
    if (data.length > 0 && focusedLoanIndex === -1) {
      setFocusedLoanIndex(0);
      setFocusedLoan(data[0]);
    } else if (data.length === 0) {
      setFocusedLoanIndex(-1);
      setFocusedLoan(null);
    }
  }, [data, focusedLoanIndex]);

  const handleDetailsClick = useCallback(
    (loanRequestId) => {
      const loan =
        data.find((loan) => loan.loan_request_id === loanRequestId) || null;
      const index = data.findIndex(
        (loan) => loan.loan_request_id === loanRequestId
      );
      setFocusedLoan(loan);
      setFocusedLoanIndex(index);
      setIsApprovalModalOpen(true);
    },
    [data]
  );

  const localUserToken = JSON.parse(localStorage.getItem("currUserToken"));
  if (!localUserToken) return <Redirect to="/signin" />;
  const localUserType = JSON.parse(localStorage.getItem("currUserType"));

  return (
    <div className="main-container">
      <div className="merchant-loan-requests-container">
        {localUserType !== "agency_user" && (
          <GeographyFilters
            {...geographyFilters}
            onFilterChange={setGeographyFilters}
            includeDatePicker={false}
            label="Merchant Loan Requests"
          />
        )}
        <div className="merchant-loan-requests">
          <LoanRequestFilter
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setActionReasonsModalOpen={setIsActionReasonModalOpen}
            allCount={counts.total}
            mdoCount={counts.mdo_pending}
            omCount={counts.om_pending}
          />
          <LoanRequestsTable
            data={data}
            loading={loading}
            error={!!error}
            page={currentPage}
            pageCount={pageCount}
            focusedLoanIndex={focusedLoanIndex}
            onDetailsClick={handleDetailsClick}
            onPageChange={(page) => setCurrentPage(page)}
            totalCount={counts.total}
            pageSize={10}
          />
          {isApprovalModalOpen && (
            <LoanApprovalModal
              key={focusedLoan?.loan_request_id}
              loan={focusedLoan}
              onClose={() => {
                setIsApprovalModalOpen(false);
              }}
              onActionComplete={mutateLoanList}
            />
          )}
          {isActionReasonModalOpen && (
            <ActionReasonsModal
              onClose={() => setIsActionReasonModalOpen(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default MerchantLoanRequests;
